import React, { useEffect, useState } from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { useToast } from "../../contexts/ToastContext";
import { useTheme } from "../../contexts/ThemeContext";

const icons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const typeStyles = {
  success: "border-l-4 border-green-500",
  error: "border-l-4 border-red-500",
  info: "border-l-4 border-blue-500",
  warning: "border-l-4 border-yellow-500",
};

const iconColors = {
  success: "text-green-500",
  error: "text-red-500",
  info: "text-blue-500",
  warning: "text-yellow-500",
};

const ToastItem = ({ toast }) => {
  const { removeToast } = useToast();
  const { colors, isDark } = useTheme();
  const Icon = icons[toast.type] || Info;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    // Wait for exit animation to finish before removing from context
    setTimeout(() => {
      removeToast(toast.id);
    }, 300);
  };

  const bgColor = isDark ? "bg-[#18181b]" : "bg-white";
  const textColor = isDark ? "text-white" : "text-gray-900";
  const borderColor = isDark ? "border-[#27272a]" : "border-gray-200";

  return (
    <div
      className={`
        transform transition-all duration-300 ease-in-out
        ${
          isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
        }
        ${bgColor} ${textColor}
        relative flex w-full max-w-sm items-start overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5
        ${typeStyles[toast.type]}
        mb-4
      `}
      role="alert"
    >
      <div className="p-4 flex w-full">
        <div className="flex-shrink-0">
          <Icon className={`h-6 w-6 ${iconColors[toast.type]}`} />
        </div>
        <div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium">{toast.message}</p>
        </div>
        <div className="ml-4 flex flex-shrink-0">
          <button
            type="button"
            className={`inline-flex rounded-md ${bgColor} text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
            onClick={handleDismiss}
          >
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const ToastContainer = () => {
  const { toasts } = useToast();

  return (
    <div
      aria-live="assertive"
      className="fixed inset-0 z-[9999] flex items-end px-4 py-6 pointer-events-none sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        {toasts.map((toast) => (
          <div key={toast.id} className="pointer-events-auto w-full max-w-sm">
            <ToastItem toast={toast} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ToastContainer;
